import { useState, useEffect, useCallback } from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";

const API_BASE_URL = "";
const CACHE_KEY = "news_cache";
const CACHE_EXPIRY = 30 * 1000; // 30 dakika

// Helper: Rich text children'ı span objelerine dönüştür (stil ve link desteği)
function parseRichTextChildren(children) {
  if (!Array.isArray(children)) return [];
  return children.map((child, idx) => {
    if (child.type === "link" && child.url) {
      return {
        type: "link",
        url: child.url,
        children: parseRichTextChildren(child.children),
        key: `link-${idx}`,
      };
    }
    return {
      type: "text",
      text: child.text || "",
      bold: !!child.bold,
      italic: !!child.italic,
      underline: !!child.underline,
      strikethrough: !!child.strikethrough,
      code: !!child.code, // Inline code desteği eklendi
      key: `span-${idx}`,
    };
  });
}

// Gelişmiş: Rich text'i düz metne çevir (linkleri, stilleri atar)
const extractTextFromContent = (content) => {
  if (!content) return "";
  if (typeof content === "string") return content;
  if (Array.isArray(content)) {
    return content
      .map((block) => {
        if (block.children && Array.isArray(block.children)) {
          return block.children
            .map((child) => {
              if (child.type === "link" && child.children) {
                return child.children.map((c) => c.text || "").join("");
              }
              return child.text || "";
            })
            .join("");
        }
        return "";
      })
      .join("\n");
  }
  return "";
};

// Extract first 3 sentences for news description
const extractFirstThreeSentences = (content) => {
  if (!content) return "";
  
  const text = extractTextFromContent(content);
  if (!text) return "";
  
  // Split by sentence endings (., !, ?)
  const sentences = text.split(/[.!?]+/).filter(sentence => sentence.trim().length > 0);
  
  // Take first 3 sentences and join them back
  const firstThree = sentences.slice(0, 3).map(sentence => sentence.trim()).join('. ');
  
  // Add ending period if not present
  return firstThree && !firstThree.match(/[.!?]$/) ? firstThree + '.' : firstThree;
};

// Format content blocks for slider/news detail
export const formatContentBlocks = (blocks) => {
  if (!blocks || !Array.isArray(blocks)) return [];
  return blocks.map((block, idx) => {
    // Headings (levels 1-6)
    if (block.type === "heading") {
      return {
        type: "heading",
        level: block.level || 2,
        text: (block.children || []).map((child) => child.text).join("") || "",
        spans: parseRichTextChildren(block.children),
        children: block.children || [],
        key: `heading-${idx}`,
      };
    }
    // Paragraph
    if (block.type === "paragraph") {
      return {
        type: "paragraph",
        text: (block.children || []).map((child) => child.text).join("") || "",
        spans: parseRichTextChildren(block.children),
        children: block.children || [],
        key: `paragraph-${idx}`,
      };
    }
    // Ordered/Unordered List
    if (block.type === "list") {
      return {
        type: block.format === "ordered" ? "ol" : "ul",
        format: block.format,
        items: Array.isArray(block.children)
          ? block.children
              .filter((item) => item.type === "list-item")
              .map((item, i) => ({
                text: (item.children || []).map((child) => child.text).join("") || "",
                spans: parseRichTextChildren(item.children),
                children: item.children || [],
                key: `list-item-${idx}-${i}`,
              }))
          : [],
        key: `list-${idx}`,
      };
    }
    // Image block
    if (block.type === "image" && block.image) {
      let fullUrl = block.image.url;
      if (fullUrl && !fullUrl.startsWith("http")) {
        fullUrl = `${API_BASE_URL}${fullUrl}`;
      }
      const width =
        block.image.width || block.image.formats?.thumbnail?.width || 256;
      const height =
        block.image.height || block.image.formats?.thumbnail?.height || 256;
      return {
        type: "image",
        image: {
          fullUrl,
          width,
          height,
          caption: block.image.caption || "",
          alt: block.image.alternativeText || block.image.name || "",
        },
        children: block.children || [],
        key: `image-${idx}`,
      };
    }
    // Quote block
    if (block.type === "quote") {
      return {
        type: "quote",
        text: (block.children || []).map((child) => child.text).join("") || "",
        spans: parseRichTextChildren(block.children),
        children: block.children || [],
        key: `quote-${idx}`,
      };
    }
    // Code block
    if (block.type === "code") {
      return {
        type: "code",
        text: (block.children || []).map((child) => child.text).join("") || "",
        language: block.language || "",
        children: block.children || [],
        key: `code-${idx}`,
      };
    }
    // Unknown/fallback
    return {
      type: block.type || "unknown",
      text: (block.children || []).map((child) => child.text).join("") || "",
      spans: parseRichTextChildren(block.children),
      children: block.children || [],
      key: `block-${idx}`,
    };
  });
};

// Fetch paginated news list directly
async function fetchNewsApi(page = 1, pageSize = 10) {
  // publishedAt'e göre sıralama
  const url = `${API_BASE_URL}/api/habers?populate=*&sort=publishedAt:desc&pagination[page]=${page}&pagination[pageSize]=${pageSize}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error("Failed to fetch news");
  const json = await res.json();
  
  // API yanıtı doğrudan kullanılıyor - normalizasyon gerekmiyor
  return { data: json.data || [], meta: json.meta };
}

// Fetch single news detail by id directly
async function fetchNewsDetailApi(newsId) {
  const url = `${API_BASE_URL}/api/habers/${newsId}?populate=*`;
  const res = await fetch(url);
  if (!res.ok) throw new Error("Failed to fetch news detail");
  const json = await res.json();
  return json.data || null;
}

// Fetch single news detail by slug
async function fetchNewsDetailBySlugApi(slug) {
  const url = `${API_BASE_URL}/api/habers?populate=*&filters[slug][$eq]=${slug}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error("Failed to fetch news by slug");
  const json = await res.json();
  return json.data && json.data.length > 0 ? json.data[0] : null;
}

export const useNews = (options = {}) => {
  const { initialLoad = true, useCache = true, pageSize = 10 } = options;

  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timestamp, setTimestamp] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isFetching, setIsFetching] = useState(false);

  // Fetch paged news from API
  const fetchNews = useCallback(
    async (pageNum = 1, pageSizeArg = pageSize) => {
      try {
        setLoading(true);
        setError(null);
        const { data, meta } = await fetchNewsApi(pageNum, pageSizeArg);
        if (!Array.isArray(data)) throw new Error("API yanıtı geçersiz");
        
        // API'den gelen veri formatını doğrudan kullan
        const formattedNews = data.map((item) => {
          // Resim URL'lerini düzelt
          let cover = null;
          if (item.kapak_fotografi?.url) {
            const formats = {};
            if (item.kapak_fotografi.formats) {
              Object.keys(item.kapak_fotografi.formats).forEach((key) => {
                formats[key] = item.kapak_fotografi.formats[key].url.startsWith('http') 
                  ? item.kapak_fotografi.formats[key].url
                  : `${API_BASE_URL}${item.kapak_fotografi.formats[key].url}`;
              });
            }
            cover = {
              url: item.kapak_fotografi.url.startsWith('http') 
                ? item.kapak_fotografi.url 
                : `${API_BASE_URL}${item.kapak_fotografi.url}`,
              formats
            };
          }

          // Galeri resimlerini düzelt
          let gallery = [];
          if (Array.isArray(item.galeri)) {
            gallery = item.galeri.map(img => {
              const formats = {};
              if (img.formats) {
                Object.keys(img.formats).forEach((key) => {
                  formats[key] = img.formats[key].url.startsWith('http') 
                    ? img.formats[key].url
                    : `${API_BASE_URL}${img.formats[key].url}`;
                });
              }
              return {
                url: img.url.startsWith('http') ? img.url : `${API_BASE_URL}${img.url}`,
                formats
              };
            });
          }

          return {
            id: item.id,
            documentId: item.documentId,
            title: item.baslik,
            slug: item.slug,
            createdAt: item.createdAt,
            updatedAt: item.updatedAt,
            publishedAt: item.publishedAt,
            yayin_tarihi: item.yayin_tarihi, // <-- eklendi
            locale: item.locale,
            contentBlocks: Array.isArray(item.icerik)
              ? formatContentBlocks(item.icerik)
              : [],
            content: extractTextFromContent(item.icerik),
            description: item.aciklama || extractFirstThreeSentences(item.icerik),
            status: item.durum,
            cover,
            gallery,
            categories: Array.isArray(item.kategorises) ? item.kategorises : [],
          };
        });

        setTimestamp(Date.now());
        // Sayfalama: ilk sayfa ise sıfırla, değilse ekle
        setNews((prev) =>
          pageNum === 1 ? formattedNews : [...prev, ...formattedNews]
        );
        setPage(pageNum);
        setHasMore(meta?.pagination?.page < meta?.pagination?.pageCount);
        
        // Cache only first page
        if (useCache && pageNum === 1) {
          await AsyncStorage.setItem(
            CACHE_KEY,
            JSON.stringify({
              news: formattedNews,
              timestamp: Date.now(),
            })
          );
        }
        return formattedNews;
      } catch {
        setError("Haberler yüklenemedi");
        return [];
      } finally {
        setLoading(false);
      }
    },
    [useCache, pageSize]
  );

  // Load from cache or API (first page)
  const loadNews = useCallback(async () => {
    setLoading(true);
    setError(null);
    if (useCache) {
      const cached = await AsyncStorage.getItem(CACHE_KEY);
      if (cached) {
        const { news: cachedNews, timestamp: cachedTimestamp } = JSON.parse(cached);
        if (Date.now() - cachedTimestamp <= CACHE_EXPIRY) {
          setNews(cachedNews);
          setTimestamp(cachedTimestamp);
          setPage(1);
          setHasMore(true);
          setLoading(false);
          return;
        }
      }
    }
    await fetchNews(1, pageSize);
    setLoading(false);
  }, [fetchNews, useCache, pageSize]);

  // Load more pages (infinite scroll için)
  const loadMoreNews = useCallback(async () => {
    if (loading || isFetching || !hasMore) return;
    setIsFetching(true);
    await fetchNews(page + 1, pageSize);
    setIsFetching(false);
  }, [fetchNews, page, pageSize, loading, hasMore, isFetching]);

  // Fetch single news detail by id
  const fetchNewsDetail = useCallback(
    async (newsId) => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchNewsDetailApi(newsId);
        if (!data) throw new Error("Haber bulunamadı");
        
        // Resim URL'lerini düzelt
        let cover = null;
        if (data.kapak_fotografi?.url) {
          const formats = {};
          if (data.kapak_fotografi.formats) {
            Object.keys(data.kapak_fotografi.formats).forEach((key) => {
              formats[key] = data.kapak_fotografi.formats[key].url.startsWith('http') 
                ? data.kapak_fotografi.formats[key].url
                : `${API_BASE_URL}${data.kapak_fotografi.formats[key].url}`;
            });
          }
          cover = {
            url: data.kapak_fotografi.url.startsWith('http') 
              ? data.kapak_fotografi.url 
              : `${API_BASE_URL}${data.kapak_fotografi.url}`,
            formats
          };
        }

        // Galeri resimlerini düzelt
        let gallery = [];
        if (Array.isArray(data.galeri)) {
          gallery = data.galeri.map(img => {
            const formats = {};
            if (img.formats) {
              Object.keys(img.formats).forEach((key) => {
                formats[key] = img.formats[key].url.startsWith('http') 
                  ? img.formats[key].url
                  : `${API_BASE_URL}${img.formats[key].url}`;
              });
            }
            
            return {
              url: img.url.startsWith('http') ? img.url : `${API_BASE_URL}${img.url}`,
              formats
            };
          });
        }
        
        return {
          id: data.id,
          documentId: data.documentId,
          title: data.baslik,
          slug: data.slug,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          publishedAt: data.publishedAt,
          yayin_tarihi: data.yayin_tarihi, // <-- eklendi
          locale: data.locale,
          contentBlocks: Array.isArray(data.icerik)
            ? formatContentBlocks(data.icerik)
            : [],
          content: extractTextFromContent(data.icerik),
          description: data.aciklama || extractFirstThreeSentences(data.icerik),
          status: data.durum,
          cover,
          gallery,
          categories: Array.isArray(data.kategorises) ? data.kategorises : [],
        };
      } catch {
        setError("Haber detayı yüklenemedi");
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Fetch single news detail by slug
  const fetchNewsDetailBySlug = useCallback(
    async (slug) => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchNewsDetailBySlugApi(slug);
        if (!data) return null;
        
        // Resim URL'lerini düzelt - En yüksek kaliteyi kullan
        let cover = null;
        if (data.kapak_fotografi?.url) {
          const formats = {};
          if (data.kapak_fotografi.formats) {
            Object.keys(data.kapak_fotografi.formats).forEach((key) => {
              formats[key] = data.kapak_fotografi.formats[key].url.startsWith('http') 
                ? data.kapak_fotografi.formats[key].url
                : `${API_BASE_URL}${data.kapak_fotografi.formats[key].url}`;
            });
          }
          // En yüksek kaliteyi seç: large > medium > small > original
          const bestUrl = data.kapak_fotografi.formats?.large?.url || 
                         data.kapak_fotografi.formats?.medium?.url || 
                         data.kapak_fotografi.url;
          
          cover = {
            url: bestUrl.startsWith('http') ? bestUrl : `${API_BASE_URL}${bestUrl}`,
            formats,
            width: data.kapak_fotografi.width,
            height: data.kapak_fotografi.height,
            alt: data.kapak_fotografi.alternativeText || data.kapak_fotografi.name || '',
            caption: data.kapak_fotografi.caption || ''
          };
        }

        // Galeri resimlerini düzelt - En yüksek kaliteyi kullan
        let gallery = [];
        if (Array.isArray(data.galeri)) {
          gallery = data.galeri.map(img => {
            const formats = {};
            if (img.formats) {
              Object.keys(img.formats).forEach((key) => {
                formats[key] = img.formats[key].url.startsWith('http') 
                  ? img.formats[key].url
                  : `${API_BASE_URL}${img.formats[key].url}`;
              });
            }
            // En yüksek kaliteyi seç
            const bestUrl = img.url || img.formats?.large?.url || img.formats?.medium?.url;
            
            return {
              url: bestUrl.startsWith('http') ? bestUrl : `${API_BASE_URL}${bestUrl}`,
              formats,
              width: img.width,
              height: img.height,
              alt: img.alternativeText || img.name || '',
              caption: img.caption || ''
            };
          });
        }
        
        return {
          id: data.id,
          documentId: data.documentId,
          title: data.baslik,
          slug: data.slug,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          publishedAt: data.publishedAt,
          yayin_tarihi: data.yayin_tarihi, // <-- eklendi
          locale: data.locale,
          contentBlocks: Array.isArray(data.icerik)
            ? formatContentBlocks(data.icerik)
            : [],
          content: extractTextFromContent(data.icerik),
          description: data.aciklama || extractFirstThreeSentences(data.icerik),
          status: data.durum,
          cover,
          gallery,
          categories: Array.isArray(data.kategorises) ? data.kategorises : [],
        };
      } catch {
        setError("Haber bulunamadı");
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (initialLoad) loadNews();
  }, [initialLoad, loadNews]);

  return {
    news,
    loading,
    error,
    timestamp,
    page,
    hasMore,
    isFetching,
    fetchNews,
    loadNews,
    loadMoreNews,
    fetchNewsDetail,
    fetchNewsDetailBySlug,
  };
};

// TÜM haberlerin sadece kapak fotoğrafı, title, slug, yayin_tarihi, description alanlarını çeken hook
import { useRef } from "react";

export const useAllNewsMeta = (options = {}) => {
  const pageSize = options.pageSize || 9;
  const imageSize = options.imageSize || "medium"; // "small", "medium", "large"

  const [newsMeta, setNewsMeta] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const totalPagesRef = useRef(1);

  // İlk yükleme veya loadMore için
  const fetchNewsMetaPage = useCallback(async (pageNum = 1) => {
    setLoading(true);
    setError(null);
    try {
      // yayin_tarihi'ne göre azalan sıralama kullan
      const url = `${API_BASE_URL}/api/habers?populate=kapak_fotografi&sort=yayin_tarihi:desc&pagination[page]=${pageNum}&pagination[pageSize]=${pageSize}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error("Haberler alınamadı");
      const json = await res.json();
      const data = json.data || [];
      const meta = json.meta;
      totalPagesRef.current = meta?.pagination?.pageCount || 1;

      // Sadece istenen alanları al
      const mapped = data.map(item => {
        let coverUrl = null;
        if (item.kapak_fotografi?.url) {
          // Belirtilen boyuta göre en iyi kaliteyi seç
          let bestFormat = item.kapak_fotografi;
          if (item.kapak_fotografi.formats) {
            if (imageSize === "small" && item.kapak_fotografi.formats.small) {
              bestFormat = item.kapak_fotografi.formats.small;
            } else if (imageSize === "medium" && item.kapak_fotografi.formats.medium) {
              bestFormat = item.kapak_fotografi.formats.medium;
            } else if (imageSize === "large" && item.kapak_fotografi.formats.large) {
              bestFormat = item.kapak_fotografi.formats.large;
            } else if (item.kapak_fotografi.formats.medium) {
              // Fallback to medium if requested size not available
              bestFormat = item.kapak_fotografi.formats.medium;
            } else if (item.kapak_fotografi.formats.small) {
              // Fallback to small if medium not available
              bestFormat = item.kapak_fotografi.formats.small;
            }
          }
          
          coverUrl = bestFormat.url.startsWith("http")
            ? bestFormat.url
            : `${API_BASE_URL}${bestFormat.url}`;
        }
        let description = item.aciklama || extractFirstThreeSentences(item.icerik);

        return {
          id: item.id,
          cover: coverUrl,
          title: item.baslik,
          slug: item.slug,
          yayin_tarihi: item.yayin_tarihi,
          description,
        };
      });

      // Sayfa 1 ise sıfırla, değilse ekle
      setNewsMeta(prev =>
        pageNum === 1 ? mapped : [...prev, ...mapped]
      );
      setPage(pageNum);
      setHasMore(pageNum < totalPagesRef.current);
      return mapped;
    } catch {
      setError("Haberler alınamadı");
      if (pageNum === 1) setNewsMeta([]);
      setHasMore(false);
      return [];
    } finally {
      setLoading(false);
    }
  }, [pageSize, imageSize]);

  // İlk yükleme
  useEffect(() => {
    fetchNewsMetaPage(1);
    // eslint-disable-next-line
  }, [pageSize, imageSize]);

  // Sonraki sayfayı yükle
  const loadMore = useCallback(async () => {
    if (loading || isFetching || !hasMore) return;
    setIsFetching(true);
    await fetchNewsMetaPage(page + 1);
    setIsFetching(false);
  }, [fetchNewsMetaPage, page, loading, isFetching, hasMore]);

  // Yeniden yükleme (ilk sayfa)
  const refetch = useCallback(() => {
    setPage(1);
    setHasMore(true);
    fetchNewsMetaPage(1);
  }, [fetchNewsMetaPage]);

  return {
    newsMeta,
    loading,
    error,
    page,
    hasMore,
    isFetching,
    loadMore,
    refetch,
  };
};

// Son 6 haberi yayin_tarihi'ne göre alan hook
export const useLatestNews = (options = {}) => {
  const imageSize = options.imageSize || "large"; // Default to large for better quality

  const [latestNews, setLatestNews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchLatestNews = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // yayin_tarihi'ne göre azalan sıralama, sadece 6 haber al
      const url = `${API_BASE_URL}/api/habers?populate=kapak_fotografi&sort=yayin_tarihi:desc&pagination[page]=1&pagination[pageSize]=6`;
      const res = await fetch(url);
      if (!res.ok) throw new Error("Son haberler alınamadı");
      const json = await res.json();
      const data = json.data || [];

      // Sadece istenen alanları al
      const mapped = data.map(item => {
        let cover = null;
        if (item.kapak_fotografi?.url) {
          const formats = {};
          
          // Tüm formatları işle
          if (item.kapak_fotografi.formats) {
            Object.keys(item.kapak_fotografi.formats).forEach((key) => {
              formats[key] = item.kapak_fotografi.formats[key].url.startsWith('http') 
                ? item.kapak_fotografi.formats[key].url
                : `${API_BASE_URL}${item.kapak_fotografi.formats[key].url}`;
            });
          }

          // En iyi URL'yi seç: imageSize'a göre küçük formatı tercih et (FULL 411KB yerine
          // large 115KB). Format yoksa kademeli olarak büyüğe, en sonda orijinale düş.
          const rawFormats = item.kapak_fotografi.formats || {};
          const pick = rawFormats[imageSize] || rawFormats.large || rawFormats.medium || rawFormats.small;
          let bestUrl = (pick && pick.url) ? pick.url : item.kapak_fotografi.url;

          cover = {
            url: bestUrl.startsWith("http") ? bestUrl : `${API_BASE_URL}${bestUrl}`,
            formats
          };
        }

        return {
          id: item.id,
          title: item.baslik,
          description: item.aciklama || extractFirstThreeSentences(item.icerik),
          yayin_tarihi: item.yayin_tarihi,
          cover,
          slug: item.slug,
        };
      });

      setLatestNews(mapped);
      return mapped;
    } catch {
      setError("Son haberler alınamadı");
      setLatestNews([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [imageSize]);

  // İlk yükleme
  useEffect(() => {
    fetchLatestNews();
  }, [fetchLatestNews]);

  // Yeniden yükleme fonksiyonu
  const refetch = useCallback(() => {
    fetchLatestNews();
  }, [fetchLatestNews]);

  return {
    latestNews,
    loading,
    error,
    refetch,
  };
};

export default useNews;